import React, { useEffect, useMemo, useState } from "react";
import {
  KeyboardArrowDown,
  PersonOutline,
  LocationOnOutlined,
  PhoneOutlined,
  CheckBox,
  CheckBoxOutlineBlank,
  StorefrontOutlined,
  WorkOutlineOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import Swal from "sweetalert2";

const StoreCard = ({ store, selectedJobs, handleStoreSelect }) => {
  const [selectedJobIndex, setSelectedJobIndex] = useState(0);

  const selectedJob = store.jobs?.[selectedJobIndex] || store.jobs?.[0];

  const selectedProduct = selectedJob?.product;

  const jobIds =
    store.jobs?.map((job) => job?.details?.id).filter(Boolean) || [];

  const isJobSelected =
    jobIds.length > 0 &&
    jobIds.every((id) => selectedJobs?.some((job) => job?.details?.id === id));

  return (
    <div
      className={`group w-full flex flex-col justify-between max-w-[400px] cursor-pointer overflow-hidden rounded-xl border bg-white shadow-sm transition-all duration-200 ${
        isJobSelected
          ? "border-[#0798f0] bg-[#f8fcff] shadow-md ring-1 ring-[#0798f0]/20"
          : "border-gray-200 hover:border-[#0798f0]/50 hover:shadow-md"
      }`}
    >
      <div>
        <div className="flex items-start justify-between border-b border-gray-100 px-4 py-3">
          <div className="min-w-0">
            <h3 className="truncate text-[14px] font-semibold text-gray-800">
              {store.details?.storeName || "Unnamed Store"}
            </h3>

            <p className="text-[11px] text-gray-400">
              {store.jobs?.length || 0} Product
              {store.jobs?.length === 1 ? "" : "s"}
            </p>
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();

              handleStoreSelect(jobIds);
            }}
            className="ml-3 flex-shrink-0 rounded-md transition hover:bg-blue-50"
          >
            {isJobSelected ? (
              <CheckBox
                sx={{
                  fontSize: 21,
                  color: "#0798f0",
                }}
              />
            ) : (
              <CheckBoxOutlineBlank
                sx={{
                  fontSize: 21,
                  color: "#94a3b8",
                }}
              />
            )}
          </button>
        </div>

        <div className="space-y-2 px-4 py-3">
          <div className="flex items-center gap-2 text-[14px] text-gray-600">
            <PersonOutline
              sx={{
                fontSize: 15,
                color: "#64748b",
              }}
            />

            <span className="truncate">
              {store.details?.storeOwnerName || "N/A"}
            </span>

            <span className="ml-auto flex items-center gap-1 whitespace-nowrap text-[14px] text-gray-500">
              <PhoneOutlined
                sx={{
                  fontSize: 14,
                  color: "#64748b",
                }}
              />

              {store.details?.storeOwnerPhoneNumber || "N/A"}
            </span>
          </div>

          <div className="flex items-start gap-2 text-[14px] leading-4 text-gray-600">
            <LocationOnOutlined
              sx={{
                fontSize: 15,
                color: "#64748b",
                marginTop: "1px",
                flexShrink: 0,
              }}
            />

            <span>
              {[
                store.details?.address,
                store.details?.city,
                store.details?.state,
                store.details?.zipCode,
              ]
                .filter(Boolean)
                .join(", ") || "Address not available"}
            </span>
          </div>
        </div>
      </div>

      <div className="w-full border-t border-blue-100 bg-gradient-to-r from-blue-50 to-sky-50 px-4 py-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#0798f0]/10">
              <span className="text-[14px]">📦</span>
            </div>

            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#64748b]">
              Product Details
            </p>
          </div>

          {store.jobs?.length > 1 && (
            <select
              value={selectedJobIndex}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();

                setSelectedJobIndex(Number(e.target.value));
              }}
              className="max-w-[150px] rounded-md border border-blue-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 outline-none focus:border-[#0798f0]"
            >
              {store?.jobs?.map((job, index) => (
                <option key={`${job?.details?.id}-${index}`} value={index}>
                  {job?.product?.productType || `Product ${index + 1}`}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
            <span className="text-[12px] font-medium uppercase tracking-wide text-gray-400">
              Type:
            </span>

            <span className="mt-0.5 block truncate text-[12px] font-semibold text-gray-700">
              {selectedProduct?.productType || "N/A"}
            </span>
          </div>

          <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
            <span className="text-[12px] font-medium uppercase tracking-wide text-gray-400">
              Approved Size:
            </span>

            <span className="mt-0.5 block truncate text-[12px] font-semibold text-gray-700">
              {selectedProduct?.approvedWidth ?? 0}
              {" × "}
              {selectedProduct?.approvedHeight ?? 0}
              {" sq. ft."}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const JobCard = ({ job, selected, handleJobSelect }) => {
  const store = job?.details;
  const product = job?.product;

  const jobId = job?.details?.id;

  return (
    <div
      onClick={() => handleJobSelect(jobId)}
      className={`w-full max-w-[400px] cursor-pointer overflow-hidden rounded-xl border bg-white shadow-sm transition-all duration-200 ${
        selected
          ? "border-[#0798f0] bg-[#f8fcff] shadow-md ring-1 ring-[#0798f0]/20"
          : "border-gray-200 hover:border-[#0798f0]/50 hover:shadow-md"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b border-gray-100 px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-[14px] font-semibold text-gray-800">
            {store?.storeName || "N/A"}
          </h3>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleJobSelect(jobId);
          }}
          className="ml-3 flex-shrink-0 rounded-md transition hover:bg-blue-50"
        >
          {selected ? (
            <CheckBox
              sx={{
                fontSize: 21,
                color: "#0798f0",
              }}
            />
          ) : (
            <CheckBoxOutlineBlank
              sx={{
                fontSize: 21,
                color: "#94a3b8",
              }}
            />
          )}
        </button>
      </div>

      {/* Store */}
      <div className="flex flex-col justify-between ">
        <div className="space-y-2 px-4 py-3">
          <div className="flex items-center gap-2 text-[14px] text-gray-600">
            <PersonOutline
              sx={{
                fontSize: 15,
                color: "#64748b",
              }}
            />

            <span className="truncate">{store?.storeOwnerName || "N/A"}</span>

            <span className="ml-auto flex items-center gap-1 whitespace-nowrap text-[14px] text-gray-500">
              <PhoneOutlined
                sx={{
                  fontSize: 14,
                  color: "#64748b",
                }}
              />

              {store?.storeOwnerPhoneNumber || "N/A"}
            </span>
          </div>

          <div className="flex items-start gap-2 text-[14px] leading-4 text-gray-600">
            <LocationOnOutlined
              sx={{
                fontSize: 15,
                color: "#64748b",
                marginTop: "1px",
                flexShrink: 0,
              }}
            />

            <span>
              {[store?.address, store?.city, store?.state, store?.zipCode]
                .filter(Boolean)
                .join(", ") || "Address not available"}
            </span>
          </div>
        </div>

        {/* Product */}
        <div className="border-t border-blue-100 bg-gradient-to-r from-blue-50 to-sky-50 px-4 py-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
              <span className="text-[11px] uppercase text-gray-400">Type</span>

              <span className="mt-0.5 block truncate text-[12px] font-semibold text-gray-700">
                {product?.productType || "N/A"}
              </span>
            </div>

            <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
              <span className="text-[11px] uppercase text-gray-400">Size</span>

              <span className="mt-0.5 block truncate text-[12px] font-semibold text-gray-700">
                {product?.approvedWidth ?? 0}
                {" × "}
                {product?.approvedHeight ?? 0}
                {" sq. ft."}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const groupStores = (stores) => {
  const grouped = new Map();

  stores.forEach((store) => {
    const details = store?.details;

    const key = [
      details?.storeOwnerPhoneNumber,
      details?.storeName,
      details?.city,
    ]
      .map((value) => value?.toString().trim().toLowerCase())
      .join("|");

    if (!grouped.has(key)) {
      grouped.set(key, {
        ...store,

        details: {
          ...store?.details,
        },

        jobs: [],

        isAssigned: store.isAssigned,
        isAssignedToCurrentPo: store?.isAssignedToCurrentPo,

        assignedPurchaseOrderId: store?.assignedPurchaseOrderId,
      });
    }

    grouped.get(key).jobs.push({
      ...store,
      product: store.product,
    });
  });

  return Array.from(grouped.values());
};

const AssignStores = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const isPOData = location.state?.poData;
  const poId = location.state?.poId || location.state?.poData?.id;
  const vendorId = location.state?.vendorId;
  const campaignId = location.state?.campaignId;

  const serverURL = process.env.REACT_APP_URL;
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);

  const [viewMode, setViewMode] = useState("store");

  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectedJobsIds, setSelectedJobsIds] = useState([]);
  const [linkedPOs, setLinkedPOs] = useState([]);

  const [allCampaignsData, setAllCampaignsData] = useState([]);

  const [selectedItem, setSelectedItem] = useState("");

  const [storesData, setStoresData] = useState([]);
  const [jobsData, setJobsData] = useState([]);

  const [selectedCampaign, setSelectedCampaign] = useState("");

  const [selectedVendor, setSelectedVendor] = useState("");
  const selectedProducts = isPOData?.items?.map((ele) => ele.item_name);

  const getStores = async (campaignId = null, vendorId = null) => {
    if (!campaignId || !vendorId || !poId) {
      setStoresData([]);
      setJobsData([]);
      return;
    }
    const selectedProductsParam = encodeURIComponent(
      JSON.stringify(selectedProducts),
    );

    try {
      setLoading(true);

      const response = await axios.get(
        `${serverURL}/purchaseOrder/linkedStores/${campaignId}/vendor/${vendorId}/stores/${poId}/${selectedProductsParam}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.data?.success) {
        const apiData = response?.data?.data || [];

        setJobsData(apiData);

        const groupedData = groupStores(apiData);

        setStoresData(groupedData);
      } else {
        setStoresData([]);
        setJobsData([]);
      }
    } catch (error) {
      console.error(
        "Error fetching stores:",
        error?.response?.data || error?.message,
      );

      setStoresData([]);
      setJobsData([]);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedStores = async (campaignId = null, vendorId = null) => {
    if (!campaignId || !vendorId || !poId) {
      setSelectedJobs([]);
      setSelectedJobsIds([]);
      return;
    }

    try {
      const response = await axios.get(
        `${serverURL}/purchaseOrder/linkedSelectedStores/${campaignId}/vendor/${vendorId}/stores/${poId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.data?.success) {
        const selectedData = response?.data?.data || [];
        let allLinkedPOs = response?.data?.allLinkedPOs || [];
        setLinkedPOs(allLinkedPOs);

        // IDs coming from API
        const jobIds = selectedData
          .map((ele) => ele?.jobsForInstallationPartnerId)
          .filter(Boolean);

        // Save IDs
        setSelectedJobsIds(jobIds);
      }
    } catch (error) {
      console.error(
        "Error fetching selected stores:",
        error?.response?.data || error?.message,
      );
    }
  };

  const getAllOngoingCampaigns = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${serverURL}/purchaseOrder/allCampaignsWithVendors`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.data?.success) {
        setAllCampaignsData(response?.data?.data || []);
      }
    } catch (error) {
      console.error(
        "Error fetching campaigns:",
        error?.response?.data || error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getPoAssignedDetails = async () => {
    if (!poId) return;

    try {
      setLoading(true);

      const response = await axios.get(
        `${serverURL}/purchaseOrder/getPoAssignedDetails/${poId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.data?.success) {
        const campaignId = response?.data?.data?.campaignId;

        const vendorId = response?.data?.data?.vendorId;

        setSelectedVendor(vendorId);
        setSelectedCampaign(campaignId);
      }
    } catch (error) {
      console.error(
        "Error fetching PO assigned details:",
        error?.response?.data || error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllOngoingCampaigns();
    if (vendorId && campaignId) {
      setSelectedCampaign(campaignId);
      setSelectedVendor(vendorId);
    }
    // getPoAssignedDetails();
  }, []);

  useEffect(() => {
    if (selectedCampaign && selectedVendor) {
      getStores(selectedCampaign, selectedVendor);
      getSelectedStores(selectedCampaign, selectedVendor);
    } else {
      setStoresData([]);
      setJobsData([]);
      setSelectedJobs([]);
    }
  }, [selectedCampaign, selectedVendor]);

  useEffect(() => {
    if (!jobsData?.length || !selectedJobsIds?.length) {
      setSelectedJobs([]);
      return;
    }

    const selectedJobObjects = jobsData.filter((job) =>
      selectedJobsIds.includes(job?.details?.id),
    );

    setSelectedJobs(selectedJobObjects);
  }, [jobsData, selectedJobsIds]);

  const selectedCampaignData = allCampaignsData.find(
    (item) => item?.campaign?._id === selectedCampaign,
  );

  const vendors = selectedCampaignData?.vendors || [];

  // const getJobArea = (job) => {
  //   const width = Number(job?.product?.approvedWidth || 0);
  //   const height = Number(job?.product?.approvedHeight || 0);

  //   return width * height;
  // };

  const handleStoreSelect = (jobIds) => {
    if (!jobIds?.length) return;

    setSelectedJobs((prev) => {
      // Check if all jobs of this store are already selected
      const allSelected = jobIds.every((id) =>
        prev.some((job) => job?.details?.id === id),
      );

      // If all are selected → remove them
      if (allSelected) {
        const updatedJobs = prev.filter(
          (job) => !jobIds.includes(job?.details?.id),
        );

        setSelectedJobsIds(updatedJobs.map((job) => job?.details?.id));

        return updatedJobs;
      }

      // Get jobs that are not already selected
      const jobsToAdd = jobsData.filter(
        (job) =>
          jobIds.includes(job?.details?.id) &&
          !prev.some(
            (selectedJob) => selectedJob?.details?.id === job?.details?.id,
          ),
      );

      if (!jobsToAdd.length) return prev;

      // Calculate area required by new jobs grouped by product type
      const newAreaByProductType = jobsToAdd.reduce((acc, job) => {
        const productType = job?.product?.productType?.trim()?.toLowerCase();

        if (!productType) return acc;

        const area = getJobArea(job);

        acc[productType] = (acc[productType] || 0) + area;

        return acc;
      }, {});

      // Validate area for every product type
      for (const [productType, newArea] of Object.entries(
        newAreaByProductType,
      )) {
        const poItem = isPOData?.items?.find(
          (item) => item?.item_name?.trim()?.toLowerCase() === productType,
        );

        if (!poItem) {
          Swal.fire({
            icon: "warning",
            title: "Product Not Found",
            text: `No PO area is available for ${productType}.`,
          });

          return prev;
        }

        const totalArea = Number(poItem?.quantity || 0);

        const selectedArea = prev
          .filter(
            (job) =>
              job?.product?.productType?.trim()?.toLowerCase() === productType,
          )
          .reduce((total, job) => {
            return total + getJobArea(job);
          }, 0);

        const remainingArea = totalArea - selectedArea;

        if (newArea > remainingArea) {
          Swal.fire({
            icon: "warning",
            title: "Area Limit Reached",
            text: `You can't select these jobs. ${productType} has only ${remainingArea} sq. ft remaining, but these jobs require ${newArea} sq. ft.`,
          });

          return prev;
        }
      }

      // Add jobs
      const updatedJobs = [...prev, ...jobsToAdd];

      // Update IDs also
      setSelectedJobsIds(updatedJobs.map((job) => job?.details?.id));

      return updatedJobs;
    });
  };
  const canSelectJob = (job) => {
    const productType = job?.product?.productType;

    const jobArea = getJobArea(job);

    const poItem = isPOData?.items?.find(
      (item) => item?.item_name?.toLowerCase() === productType?.toLowerCase(),
    );

    if (!poItem) {
      return false;
    }

    const totalArea = Number(poItem.quantity || 0);

    const currentlySelected = selectedAreaByProduct[productType] || 0;

    return currentlySelected + jobArea <= totalArea;
  };
  const handleJobSelect = (jobId) => {
    setSelectedJobs((prev) => {
      const alreadySelected = prev.some((job) => job?.details?.id === jobId);

      if (alreadySelected) {
        const updatedJobs = prev.filter((job) => job?.details?.id !== jobId);

        setSelectedJobsIds(updatedJobs.map((job) => job?.details?.id));

        return updatedJobs;
      }

      const job = jobsData.find((job) => job?.details?.id === jobId);

      if (!job) return prev;

      if (!canSelectJob(job)) {
        Swal.fire({
          icon: "warning",
          title: "Area Limit Reached",
          text: "You cannot select this job because it exceeds the available PO area.",
        });

        return prev;
      }

      const updatedJobs = [...prev, job];

      setSelectedJobsIds(updatedJobs.map((job) => job?.details?.id));

      return updatedJobs;
    });
  };
  const filteredStores = useMemo(() => {
    if (!selectedItem?.trim()) {
      return storesData;
    }

    const searchText = selectedItem.toLowerCase().trim();

    return storesData.filter((store) =>
      store.jobs?.some((job) =>
        job?.product?.productType?.toLowerCase().includes(searchText),
      ),
    );
  }, [storesData, selectedItem]);

  const filteredJobs = useMemo(() => {
    if (!selectedItem?.trim()) {
      return jobsData;
    }

    const searchText = selectedItem.toLowerCase().trim();

    return jobsData.filter((job) => {
      const productType = job?.product?.productType?.toLowerCase() || "";

      const storeName = job?.details?.storeName?.toLowerCase() || "";

      return productType.includes(searchText) || storeName.includes(searchText);
    });
  }, [jobsData, selectedItem]);

  const allStoresSelected =
    jobsData.length > 0 &&
    selectedJobs.length === jobsData.filter((job) => job?.details?.id).length;
  const allJobsSelected =
    jobsData.length > 0 &&
    selectedJobs.length === jobsData.filter((job) => job?.details?.id).length;

  const handleSelectAll = () => {
    if (allJobsSelected) {
      // Deselect all
      setSelectedJobs([]);
      setSelectedJobsIds([]);
      return;
    }

    // All jobs
    const allJobs = jobsData.filter((job) => job?.details?.id);

    // Validate area before selecting all
    const areaByProductType = allJobs.reduce((acc, job) => {
      const productType = job?.product?.productType?.trim()?.toLowerCase();

      if (!productType) return acc;

      const area = getJobArea(job);

      acc[productType] = (acc[productType] || 0) + area;

      return acc;
    }, {});

    // Check area for each product type
    for (const [productType, requiredArea] of Object.entries(
      areaByProductType,
    )) {
      const poItem = isPOData?.items?.find(
        (item) => item?.item_name?.trim()?.toLowerCase() === productType,
      );

      if (!poItem) {
        Swal.fire({
          icon: "warning",
          title: "Product Not Found",
          text: `No PO area is available for ${productType}.`,
        });

        return;
      }

      const availableArea = Number(poItem?.quantity || 0);

      if (requiredArea > availableArea) {
        Swal.fire({
          icon: "warning",
          title: "Area Limit Reached",
          text: `You can't select all jobs. ${productType} requires ${requiredArea} sq. ft, but only ${availableArea} sq. ft is available.`,
        });

        return;
      }
    }

    // Select all jobs
    setSelectedJobs(allJobs);

    setSelectedJobsIds(allJobs.map((job) => job?.details?.id).filter(Boolean));
  };

  const onViewPO = () => {
    navigate("/adminportal/viewPO", {
      state: {
        poData: isPOData,
        mode: "view",
      },
    });
  };
  const handleViewPO = async (po) => {
    try {
      // setDataLoading(true);
      let response = await axios.get(
        `${serverURL}/purchaseOrder/viewPOByPONumber`,
        {
          params: { poNumber: po?.po_number, documentId: po?.document_id },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data?.success) {
        navigate("/adminportal/viewPO", {
          state: {
            poData: response?.data?.data || response?.data?.data?.detailedData,
            mode: "view",
            poStatus: po?.rawData?.document_status,
            pdfLink: response?.data?.pdfLink,
            poId: response?.data?.poId,
            vendorId: response?.data?.vendorId,
            campaignId: response?.data?.campaignId,
          },
        });
      }
    } catch (error) {
      console.error("error", error.response);
    } finally {
      // setDataLoading(false);
    }
  };
  const getJobArea = (job) => {
    const width = Number(job?.product?.approvedWidth || 0);
    const height = Number(job?.product?.approvedHeight || 0);

    return width * height;
  };

  const selectedAreaByProduct = selectedJobs.reduce((acc, job) => {
    const productType = job?.product?.productType;

    if (!productType) return acc;

    const area = getJobArea(job);

    acc[productType] = (acc[productType] || 0) + area;

    return acc;
  }, {});

  const handleAssignStores = async () => {
    try {
      const jobIds = selectedJobs.map((ele) => ele.details?.id);
      const payload = {
        document_id: poId,
        vendorId: selectedVendor,
        campaignId: selectedCampaign,

        selectedStores: jobIds,
      };

      const response = await axios.post(
        `${serverURL}/purchaseOrder/assignStores`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.data?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success!",
          text: response?.data?.message || "Stores assigned successfully.",
          confirmButtonText: "OK",
        });

        navigate("/adminportal/purchaseOrder");
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: response?.data?.message || "Failed to assign stores.",
        });
      }
    } catch (error) {
      console.error("Error assigning stores:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "Something went wrong while assigning stores.",
      });
    }
  };

  return (
    <>
      <div className="h-[88vh] overflow-y-scroll bg-white">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h1 className="text-[16px] font-semibold text-gray-800">
            Assign {viewMode === "store" ? "Stores" : "Job"}
          </h1>

          <div className="relative flex items-center rounded-xl border  border-gray-200 bg-gray-100 p-1 shadow-sm shadow-blue-300">
            <div
              className={`absolute bottom-1 top-1 w-[calc(50%-4px)] border border-1 border-blue-600 rounded-lg bg-white shadow-md transition-all duration-300 ease-in-out ${
                viewMode === "store" ? "left-1" : "left-[50%]"
              }`}
            />

            <button
              type="button"
              onClick={() => setViewMode("store")}
              className={`relative z-10 flex min-w-[120px] items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 ${
                viewMode === "store"
                  ? "text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <StorefrontOutlined
                sx={{
                  fontSize: 18,
                  transition: "transform 300ms ease",
                  transform: viewMode === "store" ? "scale(1.1)" : "scale(1)",
                }}
              />

              <span>Store View</span>
            </button>

            <button
              type="button"
              onClick={() => setViewMode("job")}
              className={`relative z-10 flex min-w-[120px] items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 ${
                viewMode === "job"
                  ? "text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <WorkOutlineOutlined
                sx={{
                  fontSize: 18,
                  transition: "transform 300ms ease",
                  transform: viewMode === "job" ? "scale(1.1)" : "scale(1)",
                }}
              />

              <span>Job View</span>
            </button>
          </div>
        </div>

        <div className="mx-5 my-3 rounded-md border-2 border-gray-100 bg-white">
          {/* PO DETAILS */}

          <div className="px-4 pt-4">
            <div className="flex items-center justify-between">
              <h2 className="text-[16px] font-semibold text-gray-900">
                {isPOData?.po_number}
              </h2>

              <button
                type="button"
                onClick={onViewPO}
                className="text-[14px] font-medium text-[#0798f0] hover:underline"
              >
                View PO
              </button>
            </div>

            <h2 className="mt-2 text-[14px] font-semibold text-gray-900">
              Product
            </h2>

            <div className="mt-2 grid gap-3 sm:grid-cols-2 md:grid-cols-4">
              {isPOData?.items?.map((ele) => {
                const productType = ele?.item_name;

                const totalArea = Number(ele?.quantity || 0);

                const selectedArea = selectedAreaByProduct[productType] || 0;

                const remainingArea = Math.max(totalArea - selectedArea, 0);

                const percentage =
                  totalArea > 0
                    ? Math.min((selectedArea / totalArea) * 100, 100)
                    : 0;

                return (
                  <div
                    key={ele?.id || ele?._id}
                    className="rounded-md border px-3 py-2 border-gray-600"
                  >
                    <p className="text-sm font-bold text-blue-950">
                      {ele?.item_name}
                    </p>

                    {/* Total */}
                    <div className="flex items-center justify-between">
                      <p>Total Area:</p>

                      <p className="font-bold">{totalArea} sq. ft</p>
                    </div>

                    {/* Selected */}
                    <div className="flex items-center justify-between">
                      <p>Selected:</p>

                      <p className="font-bold text-blue-600">
                        {Number(selectedArea || 0).toFixed(2)} sq. ft
                      </p>
                    </div>

                    {/* Remaining */}
                    <div className="flex items-center justify-between">
                      <p>Remaining:</p>

                      <p
                        className={`font-bold ${
                          remainingArea === 0
                            ? "text-green-600"
                            : "text-orange-500"
                        }`}
                      >
                        {remainingArea} sq. ft
                      </p>
                    </div>

                    {/* Progress */}
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full rounded-full bg-blue-500 transition-all duration-300"
                        style={{
                          width: `${percentage}%`,
                        }}
                      />
                    </div>

                    <p className="mt-1 text-right text-[11px] text-gray-500">
                      {Math.round(percentage)}% selected
                    </p>

                    <div className="mt-1 flex items-center justify-between">
                      <p>Rate:</p>

                      <p className="font-bold">₹{ele?.price}/sq. ft</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mx-4 mt-4 border-t" />

          <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-3">
            {/* Campaign */}

            <div>
              <label className="mb-1 block text-[12px] font-semibold uppercase text-gray-600">
                Campaign
              </label>

              <div className="relative">
                <select
                  value={selectedCampaign}
                  onChange={(e) => {
                    setSelectedCampaign(e.target.value);

                    setSelectedVendor("");

                    setSelectedJobs([]);
                  }}
                  className="h-9 w-full appearance-none rounded-md border border-gray-300 bg-white px-3 pr-8 text-[12px] text-gray-700 outline-none focus:border-[#0798f0]"
                >
                  <option value="">Select Campaign</option>

                  {allCampaignsData.map((item) => (
                    <option
                      key={item?.campaign?._id}
                      value={item?.campaign?._id}
                    >
                      {item?.campaign?.name}
                    </option>
                  ))}
                </select>

                <KeyboardArrowDown
                  className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
                  sx={{
                    fontSize: 17,
                  }}
                />
              </div>
            </div>

            {/* Vendor */}

            <div>
              <label className="mb-1 block text-[12px] font-semibold uppercase text-gray-600">
                Vendor
              </label>

              <div className="relative">
                <select
                  value={selectedVendor}
                  onChange={(e) => {
                    setSelectedVendor(e.target.value);

                    setSelectedJobs([]);
                  }}
                  disabled={!selectedCampaign}
                  className="h-9 w-full appearance-none rounded-md border border-gray-300 bg-white px-3 pr-8 text-[12px] text-gray-700 outline-none focus:border-[#0798f0] disabled:bg-gray-100"
                >
                  <option value="">Select Vendor</option>

                  {vendors
                    .filter((vendor) => vendor?.vendorId != null)
                    .map((vendor) => (
                      <option key={vendor?.vendorId} value={vendor?.vendorId}>
                        {vendor?.vendorBusinessName}
                      </option>
                    ))}
                </select>

                <KeyboardArrowDown
                  className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
                  sx={{
                    fontSize: 17,
                  }}
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-[12px] font-semibold uppercase text-gray-600">
                Product
              </label>

              <div className="relative">
                <input
                  type="text"
                  value={selectedItem}
                  onChange={(e) => setSelectedItem(e.target.value)}
                  placeholder="Search product..."
                  className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 pr-8 text-[12px] text-gray-700 outline-none focus:border-[#0798f0]"
                />

                {selectedItem && (
                  <button
                    type="button"
                    onClick={() => setSelectedItem("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mx-4 flex items-center justify-between border-t pt-4">
            <span className="text-[14px] font-semibold uppercase text-gray-600">
              Select {viewMode === "store" ? "Stores" : "Jobs"}
            </span>

            <button
              type="button"
              onClick={handleSelectAll}
              disabled={
                viewMode === "store"
                  ? storesData.length === 0
                  : jobsData.length === 0
              }
              className="rounded-md border border-[#0798f0] px-5 py-1.5 text-[12px] font-medium text-[#0798f0] hover:bg-[#f2faff] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {viewMode === "store"
                ? allStoresSelected
                  ? "Deselect All"
                  : "Select All"
                : allJobsSelected
                  ? "Deselect All"
                  : "Select All"}
            </button>
          </div>
          <div className="mx-4 ">
            <p className="font-bold">All Linked POs</p>
            <div className="ml-2">
              {linkedPOs?.length > 0 ? (
                linkedPOs.map((ele) => (
                  <p
                    key={ele._id}
                    onClick={() => handleViewPO(ele)}
                    className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {ele.po_number}
                  </p>
                ))
              ) : (
                <p>No POs found</p>
              )}
            </div>
          </div>
          {loading ? (
            <div className="flex w-full items-center justify-center py-6">
              <CircularProgress />
            </div>
          ) : (
            <>
              {viewMode === "store" ? (
                filteredStores.length > 0 ? (
                  <div className="grid grid-cols-1 gap-2 px-4 py-3 md:grid-cols-2 lg:grid-cols-3">
                    {filteredStores.map((store) => (
                      <StoreCard
                        key={store?.details?.id}
                        store={store}
                        selectedJobs={selectedJobs}
                        handleStoreSelect={handleStoreSelect}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex w-full items-center justify-center py-8">
                    <span className="text-sm text-gray-500">
                      No Stores Found
                    </span>
                  </div>
                )
              ) : filteredJobs.length > 0 ? (
                <div className="grid grid-cols-1 gap-2 px-4 py-3 md:grid-cols-2 lg:grid-cols-3">
                  {filteredJobs.map((job, index) => {
                    const jobId = job?.details?.id;

                    return (
                      <JobCard
                        job={job}
                        selected={selectedJobsIds.some(
                          (item) => item === jobId,
                        )}
                        handleJobSelect={handleJobSelect}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="flex w-full items-center justify-center py-8">
                  <span className="text-sm text-gray-500">No Jobs Found</span>
                </div>
              )}
            </>
          )}

          <div className="flex justify-end px-4 pb-4 pt-2">
            <button
              type="button"
              disabled={
                selectedJobs.length === 0 ||
                !selectedVendor ||
                !selectedCampaign
              }
              onClick={handleAssignStores}
              className="rounded-md bg-[#0798f0] px-6 py-2 text-[12px] font-medium text-white transition hover:bg-[#0589d8] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Assign {viewMode === "store" ? "Stores" : "Jobs"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignStores;
